// components/AccountingServices.jsx
import { useContext, useEffect, useState } from "react"
import { AppContext } from "../App"
import { getInlineLoader } from "../Helpers";
import Footer from "../ones/Footer";

import { 
  IoMdCalculator, 
  IoMdTrendingUp, 
  IoMdOptions, 
  IoMdCheckmarkCircleOutline,
  IoMdAnalytics,
  IoMdConstruct,
  IoMdCall,
  IoMdArrowDropright
} from "react-icons/io";
import { FaBook, FaFileInvoiceDollar, FaUsersCog, FaChartLine } from "react-icons/fa";

export default function AccountingServices(props) {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);

  // Grouping the 9 services from the ATRIC document into 4 logical clusters
  const accountingPillars = [
    {
      id: 1,
      title: "Core Bookkeeping & Reporting",
      icon: <FaBook />,
      description: "Periodic accounting, bookkeeping, and the preparation of books of accounts and financial statements.",
      features: [
        "Periodic Bookkeeping",
        "Preparation of Books of Accounts",
        "Statutory Financial Statements"
      ],
      accent: "#1e293b",
      bgLight: "#f1f5f9"
    },
    {
      id: 2,
      title: "Management & Projections",
      icon: <FaChartLine />,
      description: "Preparation of management accounts, cash flow projections, and financial forecasts for strategic planning.",
      features: [
        "Management Accounts",
        "Cash Flow Projections",
        "Financial Forecasting"
      ],
      accent: "#d97706",
      bgLight: "#fff7ed"
    },
    {
      id: 3,
      title: "Compliance & Systems",
      icon: <IoMdConstruct />,
      description: "Setting up accounting systems, developing financial policies, and certification of statutory statements.",
      features: [
        "Accounting System Setup",
        "Financial Policy & Procedures",
        "Statutory Certification"
      ],
      accent: "#2563eb",
      bgLight: "#eff6ff"
    },
    {
      id: 4,
      title: "Advisory & Administration",
      icon: <FaUsersCog />,
      description: "Payroll administration and financial statement analysis to assist clients with regulatory compliance.",
      features: [
        "Payroll Administration",
        "Financial Statement Analysis",
        "Regulatory Compliance Support"
      ],
      accent: "#059669",
      bgLight: "#f0fdf4"
    }
  ];

  async function init() {
    setReady(false);
    setTimeout(() => setReady(true), 300);
  }

  useEffect(() => { init(); }, []);

  if (!ready) return (
    <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
      {getInlineLoader()}
    </div>
  );

  return (
    <div className="bg-white">
      {/* ================= HERO SECTION ================= */}
      <section className="py-5 border-bottom bg-light-subtle">
        <div className="container py-5">
          <div className="row align-items-center">
            <div className="col-lg-7">
              <span className="badge mb-3 px-3 py-2 fw-bold text-uppercase" 
                    style={{ background: "#c7d8fe", color: "#273872", fontSize: "11px", letterSpacing: "1px" }}>
                Professional Accounting
              </span>
              <h1 className="display-4 fw-bold text-dark mb-4">
                Precision in Numbers. <br />
                <span style={{ color: "#1b1ee0" }}>Clarity in Business.</span>
              </h1>
              <p className="lead text-muted mb-5 border-start border-4 ps-4 py-2">
                ATRIC Consultants provides comprehensive accounting solutions that keep pace with evolving regulatory requirements and financial reporting standards.
              </p>
              <div className="d-flex gap-3">
                <button 
                  className="btn btn-dark btn-lg px-4 rounded-0"
                  onClick={() => appContext.navTo({ item: 'contact_us', service: 'Accounting' })}
                >
                  Consult an Accountant
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= SERVICES GRID ================= */}
      <section className="py-5">
        <div className="container py-5">
          <div className="row g-4">
            {accountingPillars.map((pillar) => (
              <div className="col-lg-6" key={pillar.id}>
                <div className="p-4 h-100 border rounded-4 transition-all" 
                     style={{ background: "#ffffff", transition: "0.3s" }}>
                  <div className="d-flex align-items-start gap-4">
                    <div className="p-3 rounded-4 flex-shrink-0" style={{ backgroundColor: pillar.bgLight, color: pillar.accent }}>
                      {pillar.icon}
                    </div>
                    <div>
                      <h4 className="fw-bold mb-2">{pillar.title}</h4>
                      <p className="text-secondary small mb-4">{pillar.description}</p>
                      <div className="row g-2">
                        {pillar.features.map((feature, i) => (
                          <div key={i} className="col-md-6">
                            <div className="d-flex align-items-center gap-2 small fw-bold">
                              <IoMdCheckmarkCircleOutline style={{ color: pillar.accent }} />
                              {feature}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= SYSTEMS HIGHLIGHT (Point I) ================= */}
      <section className="py-5" style={{ background: "#f8fafc" }}>
        <div className="container py-4">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0 text-center">
               <div className="p-5 bg-white shadow-sm d-inline-block rounded-circle border border-warning border-4">
                  <IoMdOptions size={60} className="text-warning" />
               </div>
            </div>
            <div className="col-lg-6">
              <h2 className="fw-bold mb-3">Modern Accounting Infrastructure</h2>
              <p className="text-secondary mb-4">
                We don't just record transactions; we build the framework. ATRIC assists in setting up robust accounting systems for processing transactions accurately and efficiently.
              </p>
              <ul className="list-unstyled mb-4">
                <li className="mb-2 d-flex gap-2">
                   <IoMdCheckmarkCircleOutline className="text-success mt-1" />
                   <span>Custom System Implementation & Integration</span>
                </li>
                <li className="mb-2 d-flex gap-2">
                   <IoMdCheckmarkCircleOutline className="text-success mt-1" />
                   <span>Financial Policy & Procedures Development</span>
                </li>
              </ul>
              <button className="btn btn-outline-dark rounded-0 px-4" onClick={() => appContext.navTo({ item: 'contact_us' })}>
                Discuss System Setup <IoMdArrowDropright />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ================= CONTACT CTA ================= */}
      <section className="py-5 bg-white text-center">
        <div className="container py-5">
          <h3 className="fw-bold mb-3">Keep Pace with Financial Reporting Standards</h3>
          <p className="text-muted mb-4 mx-auto" style={{ maxWidth: "700px" }}>
            Whether you need periodic bookkeeping or high-level analysis of financial statements for decision-making purposes, ATRIC is your trusted partner.
          </p>
          <div className="d-flex justify-content-center gap-3">
            <button className="btn btn-primary btn-lg px-5 fw-bold rounded-0" onClick={() => appContext.navTo({ item: 'contact_us' })}>
              Start a Conversation
            </button>
            <a href="tel:+255717918808" className="btn btn-outline-dark btn-lg px-4 rounded-0 d-flex align-items-center gap-2">
              <IoMdCall /> +255 717 918 808
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}